import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  TestTemplateMutationUpdateArgs,
  TestTemplateUpdateFailure,
  TestTemplateUpdateInput,
  UpdateTestTemplateMutation,
} from "@/graphql/codegen/graphql"
import { UpdateTestTemplateDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateTestTemplateReturn {
  update: (input: TestTemplateUpdateInput) => Promise<void>
  isUpdating: boolean
  error?: CombinedError
}

export const useUpdateTestTemplate = (onSuccess: () => void): UseUpdateTestTemplateReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    UpdateTestTemplateMutation,
    TestTemplateMutationUpdateArgs
  >(UpdateTestTemplateDocument)

  const update = async (input: TestTemplateUpdateInput) => {
    const { data, error } = await executeMutation({ input })
    const output = data?.testTemplate.update
    const hasError = error || output?.__typename === "TestTemplateUpdateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message:
          error?.message ?? (output as TestTemplateUpdateFailure).error.message ?? "Failed to update test template.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      content: "Test template successfully updated.",
    })
    onSuccess()
  }

  return {
    update,
    isUpdating: fetching,
    error: error,
  }
}
